import React, { useEffect, useState, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import { X, XCircle, CheckCircle2, Globe, CalendarDays, MapPin, Users, Hourglass, Share2, Check, MoreHorizontal } from 'lucide-react';
import PageShell from '../components/layout/PageShell';
import RichTextField from '../components/RichTextField';
import MarkdownText from '../components/MarkdownText';

const ROLES_CLAIM = 'https://raah-portal.com/roles';
const API_URL     = process.env.REACT_APP_API_URL;
const WEBSITE_URL = 'https://raahtech.com';

const FILTER_OPTIONS = {
  type:    ['All', 'AI Workshops', 'Auth0 Workshops', 'Workforce Workshops', 'IGA Workshops', 'PAM Workshops', 'Happy Hour'],
  product: ['All', 'Okta WIC', 'Auth0', 'Okta CIS', 'Multi-product', 'Not Applicable'],
  segment: ['All', 'SMB', 'Commercial', 'Enterprise', 'Strategic', 'Not Applicable'],
};

// Workshop.date is stored as a datetime-local value ("YYYY-MM-DDTHH:mm") so it
// round-trips cleanly back into the picker when editing. Format it for display
// only; fall back to the raw value for older free-text dates entered before
// this picker existed.
function formatWorkshopDate(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
}

// Appends the end time (e.g. "– 10:00 PM") when the workshop has one set.
function formatWorkshopDateRange(date, endDate) {
  const start = formatWorkshopDate(date);
  if (!endDate) return start;
  const parsedEnd = new Date(endDate);
  if (isNaN(parsedEnd.getTime())) return start;
  const endTime = new Intl.DateTimeFormat('en-US', { timeStyle: 'short' }).format(parsedEnd);
  return `${start} – ${endTime}`;
}

// Hover text for a registration's status tag: who accepted / declined it.
const decisionTitle = (r) => {
  if (!['Confirmed', 'Declined'].includes(r.status)) return undefined;
  const verb = r.status === 'Confirmed' ? 'Accepted' : 'Declined';
  const when = r.decidedAt ? ' on ' + new Date(r.decidedAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '';
  return r.decidedByName ? `${verb} by ${r.decidedByName}${when}` : `${verb} (admin not recorded)`;
};

const STATUS_STYLES = {
  Pending:   { background: '#fff9db', color: '#e67700', border: '1px solid #ffe066' },
  Confirmed: { background: '#ebfbee', color: '#2f9e44', border: '1px solid #b2f2bb' },
  Declined:  { background: '#fff5f5', color: '#c92a2a', border: '1px solid #ffc9c9' },
  Cancelled: { background: '#f8f9fa', color: '#868e96', border: '1px solid #dee2e6' },
};

const s = {
  filterRow:    { display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' },
  select:       { padding: '6px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', background: '#fff' },
  grid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' },
  card:         (highlighted) => ({ border: highlighted ? '2px solid #fd5f0d' : '1px solid #e5e5e5', boxShadow: highlighted ? '0 0 0 3px rgba(253,95,13,0.15)' : 'none', borderRadius: '8px', padding: '18px', background: '#fff', display: 'flex', flexDirection: 'column' }),
  cardType:     { fontSize: '11px', color: '#888', marginBottom: '6px', textTransform: 'uppercase', fontWeight: '600' },
  cardTitle:    { fontSize: '14px', fontWeight: '600', marginBottom: '6px' },
  cardDesc:     { fontSize: '12px', color: '#666', marginBottom: '12px', flex: 1 },
  tags:         { display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' },
  tag:          { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', background: '#f0f0f0', fontSize: '11px', color: '#555' },
  cardFooter:   { display: 'flex', gap: '8px', marginTop: 'auto' },
  registerBtn:  { flex: 1, padding: '7px 0', background: '#fff', color: '#fd5f0d', border: '1px solid #fd5f0d', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  shareBtn:     { padding: '7px 12px', background: 'none', color: '#555', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' },
  shareBtnCopied: { padding: '7px 12px', background: '#ebfbee', color: '#2f9e44', border: '1px solid #b2f2bb', borderRadius: '6px', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' },
  registeredBadge: (status) => ({ ...STATUS_STYLES[status] || STATUS_STYLES.Pending, padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', display: 'inline-flex', alignItems: 'center' }),
  expiredTag:   { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', background: '#fff5f5', color: '#c92a2a', border: '1px solid #ffc9c9', fontSize: '11px', fontWeight: '600' },
  closedNotice: { flex: 1, textAlign: 'center', padding: '7px 0', fontSize: '12px', color: '#999' },
  adminEditBtn: { padding: '7px 12px', background: 'none', color: '#fd5f0d', border: '1px solid #fd5f0d', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  adminAddBtn:  { marginLeft: 'auto', padding: '7px 16px', background: '#fd5f0d', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },

  // Modal base
  overlay:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal:      { background: '#fff', borderRadius: '12px', padding: '28px', maxWidth: '520px', width: '100%', maxHeight: '85vh', overflowY: 'auto', position: 'relative' },
  modalClose: { position: 'absolute', top: '14px', right: '14px', background: 'none', border: 'none', cursor: 'pointer', color: '#999', display: 'flex' },
  modalTitle: { fontSize: '16px', fontWeight: '700', marginBottom: '4px' },
  modalSub:   { fontSize: '12px', color: '#888', marginBottom: '20px' },
  label:      { fontSize: '12px', fontWeight: '600', marginBottom: '4px', display: 'block', color: '#555' },
  input:      { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', marginBottom: '12px', boxSizing: 'border-box' },
  row2:       { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  submitBtn:  (dis) => ({ width: '100%', padding: '10px', background: dis ? '#fcb185' : '#fd5f0d', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: dis ? 'not-allowed' : 'pointer', marginTop: '4px' }),
  successBanner: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px', background: '#ebfbee', border: '1px solid #b2f2bb', borderRadius: '6px', color: '#2f9e44', fontSize: '13px', marginBottom: '14px' },
  errorBanner:   { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px', background: '#fdecea', border: '1px solid #f5c2be', borderRadius: '6px', color: '#b00020', fontSize: '13px', marginBottom: '14px' },
  empty:      { color: '#999', fontSize: '13px', padding: '20px 0' },
};

function RegistrationModal({ workshop, prefill, onClose, onSuccess, getToken }) {
  const [form, setForm] = useState({
    fullName: prefill.fullName || '',
    email:    prefill.email    || '',
    role:     prefill.role     || '',
    phone:    prefill.phone    || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError('');
    try {
      const token = await getToken();
      const res   = await fetch(`${API_URL}api/workshops/${workshop._id}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Registration failed.');
      onSuccess(workshop._id);
      onClose();
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <button style={s.modalClose} onClick={onClose}><X size={18} /></button>
        <div style={s.modalTitle}>Register for Workshop</div>
        <div style={s.modalSub}><MarkdownText text={workshop.title} inline /> · {formatWorkshopDateRange(workshop.date, workshop.endDate)}</div>
        {error && <div style={s.errorBanner}><XCircle size={14} /> {error}</div>}
        <form onSubmit={handleSubmit}>
          <label style={s.label}>Full Name *</label>
          <input style={s.input} value={form.fullName} onChange={e => set('fullName', e.target.value)} required />
          <label style={s.label}>Email *</label>
          <input style={s.input} type="email" value={form.email} onChange={e => set('email', e.target.value)} required />
          <div style={s.row2}>
            <div>
              <label style={s.label}>Role / Title</label>
              <input style={s.input} value={form.role} onChange={e => set('role', e.target.value)} placeholder="e.g. Solutions Engineer" />
            </div>
            <div>
              <label style={s.label}>Phone</label>
              <input style={s.input} type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+1 (555) 000-0000" />
            </div>
          </div>
          <button type="submit" style={s.submitBtn(submitting)} disabled={submitting}>
            {submitting ? 'Registering…' : 'Register Interest'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Admin-only: every registration for one workshop, grouped by status, with the same
// confirm / decline / revert actions (and emails) as the Admin dashboard.
function RegistrationsAdminModal({ workshop, onClose, getToken }) {
  const [regs, setRegs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [tab, setTab]         = useState('All');
  const [busyId, setBusyId]   = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res   = await fetch(`${API_URL}api/workshops/admin/registrations?workshopId=${workshop._id}&limit=500`, { headers: { Authorization: `Bearer ${token}` } });
        const data  = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load registrations.');
        setRegs((data.registrations || []).map(r => ({ ...r, workshopId: r.workshopId?._id || r.workshopId })));
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [workshop._id]);

  const act = async (r, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusyId(r._id); setError('');
    try {
      const token = await getToken();
      const res   = await fetch(`${API_URL}api/workshops/admin/registrations/${r._id}/${action}`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
      const data  = await res.json();
      if (!res.ok) throw new Error(data.error || 'Action failed.');
      const { status, decidedByName, decidedByEmail, decidedAt } = data.registration;
      setRegs(prev => prev.map(x => x._id === r._id ? { ...x, status, decidedByName, decidedByEmail, decidedAt } : x));
    } catch (err) { setError(err.message); }
    finally { setBusyId(null); }
  };

  const expired = !!workshop.expired;
  const counts  = regs.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
  const tabs    = ['All', 'Pending', 'Confirmed', 'Declined'];
  const shown   = tab === 'All' ? regs : regs.filter(r => r.status === tab);
  const count   = (t) => t === 'All' ? regs.length : (counts[t] || 0);

  const btn = (color) => ({ padding: '5px 10px', fontSize: '11px', fontWeight: '600', borderRadius: '6px', cursor: 'pointer', background: '#fff', color, border: `1px solid ${color}` });

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: '760px' }} onClick={e => e.stopPropagation()}>
        <button style={s.modalClose} onClick={onClose}><X size={18} /></button>
        <div style={s.modalTitle}>Registrations</div>
        <div style={s.modalSub}><MarkdownText text={workshop.title} inline /> · {formatWorkshopDateRange(workshop.date, workshop.endDate)}</div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }}>
          {['Pending', 'Confirmed', 'Declined'].map(st => (
            <div key={st} style={{ ...STATUS_STYLES[st], borderRadius: '8px', padding: '10px 14px' }}>
              <div style={{ fontSize: '22px', fontWeight: '700', lineHeight: 1 }}>{count(st)}</div>
              <div style={{ fontSize: '11px', fontWeight: '600', marginTop: '4px' }}>{st}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', flexWrap: 'wrap' }}>
          {tabs.map(t => (
            <button key={t} onClick={() => setTab(t)} style={{ padding: '5px 12px', fontSize: '12px', fontWeight: '600', borderRadius: '16px', cursor: 'pointer', border: '1px solid ' + (tab === t ? '#fd5f0d' : '#ddd'), background: tab === t ? '#fd5f0d' : '#fff', color: tab === t ? '#fff' : '#555' }}>
              {t} ({count(t)})
            </button>
          ))}
        </div>

        {error && <div style={s.errorBanner}><XCircle size={14} /> {error}</div>}

        {loading ? <div style={s.empty}>Loading registrations…</div>
          : shown.length === 0 ? <div style={s.empty}>No {tab === 'All' ? '' : tab.toLowerCase() + ' '}registrations.</div>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {shown.map(r => (
                <div key={r._id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', border: '1px solid #eee', borderRadius: '8px', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '600' }}>{r.fullName}</div>
                    <div style={{ fontSize: '12px', color: '#666', wordBreak: 'break-all' }}>{r.email}</div>
                    <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                      {[r.role, r.phone, 'Registered ' + new Date(r.createdAt).toLocaleDateString()].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <span style={{ ...s.registeredBadge(r.status), cursor: decisionTitle(r) ? 'help' : undefined }} title={decisionTitle(r)}>{r.status}</span>
                  <div style={{ display: 'flex', gap: '8px', minWidth: '150px', justifyContent: 'flex-end' }}>
                    {busyId === r._id ? <span style={{ fontSize: '12px', color: '#999' }}>Working…</span> : (
                      <>
                        {r.status === 'Pending' && !expired && (
                          <>
                            <button style={btn('#2f9e44')} onClick={() => act(r, 'confirm', `Confirm ${r.fullName}? This emails ${r.email} their confirmation.`)}>Confirm</button>
                            <button style={btn('#c92a2a')} onClick={() => act(r, 'decline', `Decline ${r.fullName}'s registration (${r.email})?`)}>Decline</button>
                          </>
                        )}
                        {r.status === 'Pending' && expired && <span style={{ fontSize: '12px', color: '#999' }}>Event expired</span>}
                        {r.status === 'Confirmed' && (
                          <button style={btn('#c92a2a')} onClick={() => act(r, 'revert', `Revert ${r.fullName} to DECLINED?\n\nThis emails ${r.email} an apology for the earlier acceptance and informs them the registration is declined.`)}>Revert to Declined</button>
                        )}
                        {r.status === 'Declined' && !expired && (
                          <button style={btn('#2f9e44')} onClick={() => act(r, 'revert', `Revert ${r.fullName} to CONFIRMED?\n\nThis emails ${r.email} an apology for the earlier denial along with their confirmation.`)}>Revert to Confirmed</button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

function WorkshopFormModal({ existing, onClose, onSave, getToken }) {
  const blank = { title: '', type: 'AI Workshops', product: 'Okta WIC', segment: 'Enterprise', date: '', endDate: '', slug: '', displayLocation: '', fullLocation: '', description: '', capacity: '', isPublic: false };
  const [form, setForm]     = useState(existing || blank);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const token  = await getToken();
      const method = existing ? 'PATCH' : 'POST';
      const url    = existing ? `${API_URL}api/workshops/${existing._id}` : `${API_URL}api/workshops`;
      const res    = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, capacity: form.capacity ? parseInt(form.capacity) : null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed.');
      onSave(data.workshop);
      onClose();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <button style={s.modalClose} onClick={onClose}><X size={18} /></button>
        <div style={s.modalTitle}>{existing ? 'Edit Workshop' : 'Add Workshop'}</div>
        <div style={{ height: '16px' }} />
        {error && <div style={s.errorBanner}><XCircle size={14} /> {error}</div>}
        <form onSubmit={handleSave}>
          <label style={s.label}>Title * <span style={{ color: '#aaa', fontWeight: 400 }}>(supports **bold**, *italic*, underline)</span></label>
          <RichTextField rows={2} value={form.title} onChange={v => set('title', v)} required />

          <div style={s.row2}>
            <div>
              <label style={s.label}>Type *</label>
              <select style={s.input} value={form.type} onChange={e => set('type', e.target.value)}>
                {FILTER_OPTIONS.type.slice(1).map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Product *</label>
              <select style={s.input} value={form.product} onChange={e => set('product', e.target.value)}>
                {FILTER_OPTIONS.product.slice(1).map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>

          <div style={s.row2}>
            <div>
              <label style={s.label}>Segment *</label>
              <select style={s.input} value={form.segment} onChange={e => set('segment', e.target.value)}>
                {FILTER_OPTIONS.segment.slice(1).map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Capacity</label>
              <input style={s.input} type="number" value={form.capacity} onChange={e => set('capacity', e.target.value)} placeholder="Leave blank for unlimited" />
            </div>
          </div>

          <div style={s.row2}>
            <div>
              <label style={s.label}>Start Date & Time *</label>
              <input style={s.input} type="datetime-local" value={form.date} onChange={e => set('date', e.target.value)} required />
            </div>
            <div>
              <label style={s.label}>End Date & Time <span style={{ color: '#aaa', fontWeight: 400 }}>(optional)</span></label>
              <input style={s.input} type="datetime-local" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
            </div>
          </div>

          <label style={s.label}>Display Location <span style={{ color: '#aaa', fontWeight: 400 }}>(shown publicly)</span></label>
          <input style={s.input} value={form.displayLocation} onChange={e => set('displayLocation', e.target.value)} placeholder="e.g. New York, NY" />

          <label style={s.label}>Full Location <span style={{ color: '#aaa', fontWeight: 400 }}>(sent in confirmation email only)</span></label>
          <input style={s.input} value={form.fullLocation} onChange={e => set('fullLocation', e.target.value)} placeholder="e.g. 123 Main St, Suite 400, New York, NY 10001" />

          <label style={s.label}>Description <span style={{ color: '#aaa', fontWeight: 400 }}>(supports **bold**, *italic*, underline, line breaks)</span></label>
          <RichTextField rows={4} value={form.description} onChange={v => set('description', v)} />

          <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!form.isPublic} onChange={e => set('isPublic', e.target.checked)} />
            Make public <span style={{ color: '#aaa', fontWeight: 400 }}>(displays on raahtech.com — anyone can register, no partner login required)</span>
          </label>

          {form.isPublic && (
            <>
              <label style={s.label}>Friendly URL <span style={{ color: '#aaa', fontWeight: 400 }}>(optional, e.g. Oktane2026-happyhour)</span></label>
              <input style={s.input} value={form.slug} onChange={e => set('slug', e.target.value.trim())} placeholder="Oktane2026-happyhour" />
            </>
          )}

          {existing && form.isPublic && (
            <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {form.slug && (
                <div style={{ ...s.tag, display: 'inline-block', wordBreak: 'break-all', width: 'fit-content' }}>
                  {`${WEBSITE_URL}/events/s/${form.slug}`}
                </div>
              )}
              <div style={{ ...s.tag, display: 'inline-block', wordBreak: 'break-all', width: 'fit-content' }}>
                {`${WEBSITE_URL}/events/live/${existing._id}`}
              </div>
            </div>
          )}

          <button type="submit" style={s.submitBtn(saving)} disabled={saving}>
            {saving ? 'Saving…' : existing ? 'Save Changes' : 'Create Workshop'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Public workshops get a shareable link on raahtech.com that anyone can open
// without logging in; private ones fall back to a deep link into the portal
// itself, which only resolves for authenticated partners.
function getShareUrl(workshop) {
  if (workshop.isPublic) return workshop.slug ? `${WEBSITE_URL}/events/s/${workshop.slug}` : `${WEBSITE_URL}/events/live/${workshop._id}`;
  return `${window.location.origin}/workshops?id=${workshop._id}`;
}

export default function Workshops() {
  const { getAccessTokenSilently, user } = useAuth0();
  const roles   = user?.[ROLES_CLAIM] || [];
  const isAdmin = roles.includes('Partner Portal Admin');
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('id');
  const cardRefs = useRef({});

  const [workshops,     setWorkshops]     = useState([]);
  const [myRegs,        setMyRegs]        = useState({});   // workshopId -> registration
  const [loading,       setLoading]       = useState(true);
  const [typeFilter,    setTypeFilter]    = useState('All');
  const [productFilter, setProductFilter] = useState('All');
  const [segmentFilter, setSegmentFilter] = useState('All');
  const [prefill,       setPrefill]       = useState({});

  const [regTarget,     setRegTarget]     = useState(null);  // workshop to register for
  const [editTarget,    setEditTarget]    = useState(null);  // workshop to edit (admin)
  const [regsTarget,    setRegsTarget]    = useState(null);  // workshop whose registrations modal is open (admin)
  const [showAdd,       setShowAdd]       = useState(false);
  const [successMsg,    setSuccessMsg]    = useState('');
  const [copiedId,      setCopiedId]      = useState(null);  // workshop just copied to clipboard

  const getToken = () => getAccessTokenSilently({
    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
  });

  useEffect(() => {
    async function fetchAll() {
      try {
        const token   = await getToken();
        const headers = { Authorization: `Bearer ${token}` };
        const params  = new URLSearchParams();
        if (typeFilter    !== 'All') params.set('type',    typeFilter);
        if (productFilter !== 'All') params.set('product', productFilter);
        if (segmentFilter !== 'All') params.set('segment', segmentFilter);

        const [wsRes, regRes, profileRes] = await Promise.all([
          fetch(`${API_URL}api/workshops?${params}`,              { headers }),
          fetch(`${API_URL}api/workshops/my/registrations`,       { headers }),
          fetch(`${API_URL}api/workshops/profile/me`,             { headers }),
        ]);

        const wsData      = await wsRes.json();
        const regData     = await regRes.json();
        const profileData = await profileRes.json();

        setWorkshops(wsData.workshops || []);

        // Map workshopId -> registration for quick lookup
        const regMap = {};
        for (const r of (regData.registrations || [])) {
          regMap[r.workshopId?._id || r.workshopId] = r;
        }
        setMyRegs(regMap);

        // Pre-fill from saved profile, fallback to Auth0 user
        const p = profileData.profile || {};
        setPrefill({
          fullName: p.fullName || user?.name  || '',
          email:    p.email    || user?.email || '',
          role:     p.role     || '',
          phone:    p.phone    || '',
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchAll();
  }, [typeFilter, productFilter, segmentFilter]);

  // Scroll a shared workshop (?id=...) into view once it's loaded.
  useEffect(() => {
    if (!highlightId) return;
    const el = cardRefs.current[highlightId];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightId, workshops]);

  const handleShare = async (workshop) => {
    const url = getShareUrl(workshop);
    const shareData = { title: workshop.title, text: `Check out this RAAH workshop: ${workshop.title}`, url };
    if (navigator.share && navigator.canShare?.(shareData)) {
      try { await navigator.share(shareData); return; }
      catch (err) { if (err.name === 'AbortError') return; /* fall through to clipboard copy */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(workshop._id);
      setTimeout(() => setCopiedId(prev => (prev === workshop._id ? null : prev)), 2000);
    } catch (err) { console.error(err); }
  };

  const handleRegistered = (workshopId) => {
    setMyRegs(prev => ({ ...prev, [workshopId]: { status: 'Pending' } }));
    setSuccessMsg('Registration submitted! You\'ll receive a confirmation email once approved.');
    setTimeout(() => setSuccessMsg(''), 5000);
  };

  const handleSaveWorkshop = (workshop) => {
    setWorkshops(prev => {
      const idx = prev.findIndex(w => w._id === workshop._id);
      if (idx >= 0) { const updated = [...prev]; updated[idx] = workshop; return updated; }
      return [workshop, ...prev];
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this workshop?')) return;
    try {
      const token = await getToken();
      await fetch(`${API_URL}api/workshops/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setWorkshops(prev => prev.filter(w => w._id !== id));
    } catch (err) { console.error(err); }
  };

  return (
    <PageShell title="Workshops & Events" desc="Browse available workshops and register your interest.">

      {successMsg && <div style={s.successBanner}><CheckCircle2 size={14} /> {successMsg}</div>}

      <div style={s.filterRow}>
        <select style={s.select} value={typeFilter}    onChange={e => setTypeFilter(e.target.value)}>
          {FILTER_OPTIONS.type.map(o    => <option key={o}>{o}</option>)}
        </select>
        <select style={s.select} value={productFilter} onChange={e => setProductFilter(e.target.value)}>
          {FILTER_OPTIONS.product.map(o => <option key={o}>{o}</option>)}
        </select>
        <select style={s.select} value={segmentFilter} onChange={e => setSegmentFilter(e.target.value)}>
          {FILTER_OPTIONS.segment.map(o => <option key={o}>{o}</option>)}
        </select>
        {isAdmin && (
          <button style={s.adminAddBtn} onClick={() => setShowAdd(true)}>+ Add Workshop</button>
        )}
      </div>

      {loading ? (
        <div style={s.empty}>Loading workshops…</div>
      ) : workshops.length === 0 ? (
        <div style={s.empty}>No workshops match your filters.</div>
      ) : (
        <div style={s.grid}>
          {workshops.map(w => {
            const reg = myRegs[w._id];
            const justCopied = copiedId === w._id;
            return (
              <div key={w._id} ref={el => { cardRefs.current[w._id] = el; }} style={{ ...s.card(w._id === highlightId), position: 'relative' }}>
                {isAdmin && (
                  <button
                    title="View & manage registrations"
                    onClick={() => setRegsTarget(w)}
                    style={{ position: 'absolute', top: '10px', right: '10px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', color: '#555' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#fd5f0d'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = '#fd5f0d'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#555'; e.currentTarget.style.borderColor = '#ddd'; }}
                  ><MoreHorizontal size={16} /></button>
                )}
                <div style={{ ...s.cardType, paddingRight: isAdmin ? '34px' : 0 }}>{w.type}{w.isPublic && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#fd5f0d', marginLeft: '6px' }}>· <Globe size={11} /> Public</span>}</div>
                <MarkdownText text={w.title} inline style={s.cardTitle} />
                <MarkdownText text={w.description} style={s.cardDesc} />
                <div style={s.tags}>
                  <span style={s.tag}>{w.product}</span>
                  <span style={s.tag}>{w.segment}</span>
                  <span style={s.tag}><CalendarDays size={11} /> {formatWorkshopDateRange(w.date, w.endDate)}</span>
                  {w.displayLocation && <span style={s.tag}><MapPin size={11} /> {w.displayLocation}</span>}
                  {w.capacity && <span style={s.tag}><Users size={11} /> {w.capacity} seats</span>}
                  {w.expired && <span style={s.expiredTag}>Expired</span>}
                </div>
                <div style={s.cardFooter}>
                  {reg ? (
                    <span style={s.registeredBadge(reg.status)}>
                      {reg.status === 'Pending' ? <Hourglass size={12} /> : reg.status === 'Confirmed' ? <CheckCircle2 size={12} /> : reg.status === 'Declined' ? <XCircle size={12} /> : '—'} {reg.status}
                    </span>
                  ) : w.expired ? (
                    <span style={s.closedNotice}>Registration closed</span>
                  ) : (
                    <button style={s.registerBtn} onClick={() => setRegTarget(w)}>Register Interest</button>
                  )}
                  <button
                    style={justCopied ? s.shareBtnCopied : s.shareBtn}
                    onClick={() => handleShare(w)}
                    title={w.isPublic ? 'Share the public event link' : 'Copy a private portal link (partner login required)'}
                  >
                    {justCopied ? <><Check size={12} /> Copied</> : <><Share2 size={12} /> Share</>}
                  </button>
                  {isAdmin && (
                    <>
                      <button style={s.adminEditBtn} onClick={() => setEditTarget(w)}>Edit</button>
                      <button style={{ ...s.adminEditBtn, color: '#c92a2a', borderColor: '#ffc9c9' }} onClick={() => handleDelete(w._id)}>Remove</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {regTarget && (
        <RegistrationModal
          workshop={regTarget}
          prefill={prefill}
          onClose={() => setRegTarget(null)}
          onSuccess={handleRegistered}
          getToken={getToken}
        />
      )}

      {regsTarget && (
        <RegistrationsAdminModal workshop={regsTarget} onClose={() => setRegsTarget(null)} getToken={getToken} />
      )}

      {(showAdd || editTarget) && (
        <WorkshopFormModal
          existing={editTarget || null}
          onClose={() => { setShowAdd(false); setEditTarget(null); }}
          onSave={handleSaveWorkshop}
          getToken={getToken}
        />
      )}
    </PageShell>
  );
}